import { useState } from 'react';

/**
 * Runs the calendar buffer sync and tracks its status/message.
 * syncBufferLessons: async () => axios response (from App.js)
 * onSynced: called after a successful request so the slot list can reload
 */
export default function useBufferSync(syncBufferLessons, onSynced) {
  const [isSyncingBuffers, setIsSyncingBuffers] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null); // 'success', 'error', null
  const [syncMessage, setSyncMessage] = useState('');
  // A summary pop-up that stays on screen until the coach closes it: { kind: 'success'|'warning'|'error', title, lines[], seconds }
  const [report, setReport] = useState(null);

  const runSync = async () => {
      setIsSyncingBuffers(true);
      setSyncStatus(null);
      setSyncMessage('');
      try {
        const result = await syncBufferLessons();
        const { created, updated, deleted, archived, orphanedCoachSlotsArchived, pastDueSlotsArchived, duplicateSlotsArchived, duplicateConflicts, errors } = result.data?.result || {};
        const totalProcessed = (created || 0) + (updated || 0) + (deleted || 0) + (archived || 0);

        // "Cleaned up" used to lump deleted and archived together, so
        // a slot that got archived (kept, still visible with an
        // "Archived" badge) looked identical in this message to one
        // that was actually removed - the coach would see "X cleaned
        // up" and then find the slot still sitting in their list,
        // reading as "it didn't delete properly". Reporting them
        // separately, and splitting out orphaned/past-due/duplicate
        // slots (ones with no valid coach behind them, whose time has
        // simply passed, or that double up another slot at the same
        // time) from ones archived because they have real bookings,
        // makes clear what actually happened to each kind.
        const ownArchived = (archived || 0) - (orphanedCoachSlotsArchived || 0) - (pastDueSlotsArchived || 0) - (duplicateSlotsArchived || 0);
        const parts = [];
        if (created) parts.push(`${created} created`);
        if (updated) parts.push(`${updated} updated`);
        if (deleted) parts.push(`${deleted} deleted`);
        if (ownArchived > 0) parts.push(`${ownArchived} archived (has bookings - tap 🗑️ on the slot to fully remove)`);
        if (orphanedCoachSlotsArchived) parts.push(`${orphanedCoachSlotsArchived} fake/orphaned slot${orphanedCoachSlotsArchived !== 1 ? 's' : ''} removed`);
        if (pastDueSlotsArchived) parts.push(`${pastDueSlotsArchived} past-due slot${pastDueSlotsArchived !== 1 ? 's' : ''} removed`);
        if (duplicateSlotsArchived) parts.push(`${duplicateSlotsArchived} duplicate slot${duplicateSlotsArchived !== 1 ? 's' : ''} removed`);
        if (duplicateConflicts) parts.push(`⚠️ ${duplicateConflicts} double-booking conflict${duplicateConflicts !== 1 ? 's' : ''} need manual review`);

        // The backend can report success (HTTP 200) while still
        // having hit per-slot errors internally (e.g. a calendar API
        // call failing for one slot) - previously this branch always
        // showed a plain green "✅ Synced!" regardless, so a sync
        // that silently failed to clean anything up still looked
        // like a success with nothing to investigate.
        const errorSamples = result.data?.result?.errorSamples || [];
        const seconds = result.data?.result?.durationMs ? Math.max(0.1, result.data.result.durationMs / 1000) : null;
        const lines = parts.length > 0 ? parts : (errors > 0 ? ['No changes were made.'] : ['Everything was already up to date - nothing to change.']);
        setReport(errors > 0
          ? { kind: 'warning', title: `Sync finished with ${errors} error${errors !== 1 ? 's' : ''}`, lines: [...lines, ...errorSamples.map(e => `Reason: ${e}`), 'Some slots may not have been processed. Try again in a moment.'], seconds }
          : { kind: 'success', title: 'Buffer sync finished', lines, seconds });

        if (errors > 0) {
          setSyncStatus('error');
          setSyncMessage(`⚠️ Synced with ${errors} error${errors !== 1 ? 's' : ''} - ${parts.length > 0 ? parts.join(', ') : 'no changes'}. Some slots may not have been processed; try again.`);
        } else {
          setSyncStatus('success');
          setSyncMessage(`✅ Synced! ${totalProcessed > 0 ? parts.join(', ') : 'No changes'}`);
        }

        // Clear message after 5 seconds
        setTimeout(() => {
          setSyncStatus(null);
          setSyncMessage('');
        }, 5000);

        // Let the slots tab reload its list
        if (onSynced) onSynced();
      } catch (error) {
        setSyncStatus('error');

        // Extract error message from different error sources
        let errorMsg = 'Unknown error';
        if (error?.response?.data?.message) {
          errorMsg = error.response.data.message;
        } else if (error?.response?.data?.error) {
          errorMsg = error.response.data.error;
        } else if (error?.message) {
          errorMsg = error.message;
        }

        console.error('Sync error details:', error);
        setSyncMessage(`❌ Sync failed: ${errorMsg}`);
        setReport({ kind: 'error', title: 'Sync failed', lines: [errorMsg, 'Nothing was changed. Check your connection and try again.'], seconds: null });

        // Clear message after 5 seconds
        setTimeout(() => {
          setSyncStatus(null);
          setSyncMessage('');
        }, 5000);
      } finally {
        setIsSyncingBuffers(false);
      }
  };

  const closeReport = () => setReport(null);

  return { isSyncingBuffers, syncStatus, syncMessage, runSync, report, closeReport };
}
